import { useRef, useState } from 'react';
import { ModalShell } from '@/shared/ui/ModalShell';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { FormField, fieldInputStyle } from '@/shared/ui/FormField';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { semantic, typography, radius } from '@/shared/theme/tokens';
import { useToast, useApiErrorToast } from '@/shared/ui/Toast';
import { sanitizeSvg } from '@/features/overviewDiagram/hooks/useSvgMarkup';
import { validateSvgMarkup } from '../lib/validateSvgTemplate';
import { useCreateSvgTemplate, useUpdateSvgTemplate } from '../hooks/useSvgTemplateAdmin';
import { SvgThumbnail } from './SvgThumbnail';

/**
 * Create / edit an SVG template. On create, a file is required. On edit, name
 * and/or file are optional (rename, replace, or both). The picked SVG is
 * client-validated (mirrors BE 422 rules) and its regions previewed before upload.
 */
export function SvgTemplateForm({ mode, template, onClose }) {
  const c = useThemeColors();
  const toast = useToast();
  const toastApiError = useApiErrorToast();
  const createT = useCreateSvgTemplate();
  const updateT = useUpdateSvgTemplate();
  const fileRef = useRef(null);

  const [name, setName] = useState(template?.name ?? '');
  const [file, setFile] = useState(null);
  const [markup, setMarkup] = useState(null);     // sanitized preview markup of the picked file
  const [parsed, setParsed] = useState(null);     // { ok, errors, sections }
  const [error, setError] = useState('');
  const pending = createT.isPending || updateT.isPending;

  async function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError('');
    if (!f.name.toLowerCase().endsWith('.svg')) { setError('Please choose an .svg file.'); return; }
    const text = await f.text();
    const result = validateSvgMarkup(text);
    setParsed(result);
    setFile(f);
    setMarkup(result.sections.length || result.ok ? sanitizeSvg(text) : null);
    if (mode === 'create' && !name.trim()) setName(f.name.replace(/\.svg$/i, ''));
  }

  function validate() {
    if (!name.trim()) return 'Name is required.';
    if (mode === 'create' && !file) return 'Choose an SVG file.';
    if (file && parsed && !parsed.ok) return 'Fix the SVG issues before uploading.';
    return null;
  }

  async function handleSubmit() {
    const err = validate();
    if (err) { setError(err); return; }
    try {
      if (mode === 'create') {
        await createT.mutateAsync({ name: name.trim(), file });
        toast({ message: 'Template created' });
      } else {
        const payload = {};
        if (name.trim() !== template.name) payload.name = name.trim();
        if (file) payload.file = file;
        if (!payload.name && !payload.file) { onClose(); return; }
        await updateT.mutateAsync({ id: template.id, payload });
        toast({ message: 'Template updated' });
      }
      onClose();
    } catch (e2) {
      // Surface BE 422 INVALID_SVG_TEMPLATE message (normalized to err.message).
      toastApiError(e2);
    }
  }

  const previewTemplate = template; // edit: show current svg until a new file is picked

  return (
    <ModalShell
      onClose={onClose}
      icon="image"
      iconVariant="primary"
      title={mode === 'create' ? 'Add SVG Template' : 'Edit SVG Template'}
      subtitle={mode === 'create' ? 'Upload an HMI diagram for use across deployments' : 'Rename or replace the diagram'}
      maxWidth="560px"
      footer={
        <>
          <PrimaryButton variant="secondary" onClick={onClose} disabled={pending}>Cancel</PrimaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={pending} icon={mode === 'create' ? 'upload' : 'check'}>
            {pending ? 'Saving…' : mode === 'create' ? 'Upload' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: typography.fontFamily.body }}>
        <FormField label="Name" required>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Blast Furnace" style={fieldInputStyle(c, false)} />
        </FormField>

        <FormField label={mode === 'create' ? 'SVG file' : 'Replace SVG (optional)'} required={mode === 'create'} hint="Each clickable region needs data-section=&quot;id&quot;. See the SVG authoring guide.">
          <input ref={fileRef} type="file" accept=".svg,image/svg+xml" onChange={handleFile} style={{ display: 'none' }} />
          <button onClick={() => fileRef.current?.click()} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: radius.lg,
            border: `1.5px dashed ${c.borderInput}`, background: c.bgSubtle, cursor: 'pointer', color: c.textLabel, fontSize: typography.fontSize.md,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>upload_file</span>
            {file ? file.name : 'Choose .svg file'}
          </button>
        </FormField>

        {/* Validation feedback */}
        {parsed && !parsed.ok && (
          <div style={{ padding: '8px 10px', borderRadius: radius.md, background: semantic.errorContainer, border: `1px solid ${semantic.errorBorder}` }}>
            <div style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, color: semantic.error, marginBottom: 4 }}>Fix before upload:</div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: typography.fontSize.base, color: c.textLabel }}>
              {parsed.errors.map((e) => <li key={e}>{e}</li>)}
            </ul>
          </div>
        )}

        {/* Preview: thumbnail + derived regions */}
        {(markup || previewTemplate) && (
          <div>
            <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: c.textSubtle, marginBottom: 6 }}>
              {markup ? 'Preview' : 'Current'}
            </div>
            <SvgThumbnail template={previewTemplate} rawMarkup={markup ?? undefined} height={170} />
            {(parsed?.sections?.length ?? template?.sections?.length) > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {(parsed?.sections ?? template?.sections ?? []).map((s) => (
                  <span key={s.id} style={{ fontSize: typography.fontSize.sm, padding: '2px 8px', borderRadius: radius.sm, background: c.bgSubtle, border: `1px solid ${c.borderSubtle}`, color: c.textLabel }}>
                    {s.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {error && <div style={{ fontSize: typography.fontSize.base, color: semantic.error }}>{error}</div>}
      </div>
    </ModalShell>
  );
}
